import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Card, Table } from 'react-bootstrap';
import dayjs from 'dayjs';

import { useDonorSubscriptions } from 'hooks/useDonors';
import { useCancelSubscription } from 'hooks/useStripePayments';
import { useAuthContext } from 'context/authContext';
import SoftBadge from 'components/common/SoftBadge';
import CancellationReasonModal from 'components/common/modals/CancellationReasonModal';
import { formatMoney } from 'helpers/utils';

const statusBadgeMap = {
  active: 'success',
  past_due: 'danger',
  cancelled: 'warning'
};

const DonorSubscriptions = () => {
  const { id } = useParams();
  const { subscriptions, isLoading } = useDonorSubscriptions(id);
  const { cancelSubscriptionAsync, isCancelling } = useCancelSubscription();
  const { isOrganizationAdmin, isAdmin, user } = useAuthContext();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(null);

  const handleCancelClick = subscriptionId => {
    setSelectedSubscriptionId(subscriptionId);
    setShowCancelModal(true);
  };

  const handleConfirmCancel = async (reason, notes) => {
    try {
      const sub = (subscriptions || []).find(
        s => s.stripe_subscription_id === selectedSubscriptionId
      );
      await cancelSubscriptionAsync({
        subscriptionId: selectedSubscriptionId,
        reason,
        notes,
        donorId: id,
        branchId: sub?.branch_id,
        amount: sub?.amount
      });
      setShowCancelModal(false);
      setSelectedSubscriptionId(null);
    } catch (error) {
      // Error toast handled by the hook
    }
  };

  const staffName = [user?.first_name, user?.last_name]
    .filter(Boolean)
    .join(' ');

  if (isLoading) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (!subscriptions?.length) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <p className="text-muted mb-0">No active subscriptions</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <Card.Header>
          <h5 className="mb-0">Subscriptions</h5>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive className="mb-0">
            <thead>
              <tr>
                <th>Amount</th>
                <th>Status</th>
                <th>Items</th>
                <th>Failed</th>
                <th>Cancelled</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map(sub => (
                <tr key={sub.id}>
                  <td>{formatMoney(sub.amount)}</td>
                  <td>
                    <SoftBadge
                      bg={statusBadgeMap[sub.status] || 'secondary'}
                      pill
                    >
                      {sub.status === 'past_due'
                        ? 'Payment Failed'
                        : sub.status?.charAt(0).toUpperCase() +
                          sub.status?.slice(1)}
                    </SoftBadge>
                  </td>
                  <td>
                    {sub.order_details_snapshot &&
                    Array.isArray(sub.order_details_snapshot) ? (
                      <ul className="list-unstyled mb-0">
                        {sub.order_details_snapshot.map((item, i) => (
                          <li key={i}>
                            {item.package_title ||
                              item.donation_type_name ||
                              'Donation'}{' '}
                            | {formatMoney(item.amount)}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="text-nowrap">
                    {sub.payment_failed_at ? (
                      <span
                        className={
                          sub.status === 'past_due' ? 'text-danger' : 'text-600'
                        }
                        title={
                          sub.status === 'past_due'
                            ? 'Payment failed - still unpaid'
                            : 'Payment failed on this date, since resolved'
                        }
                      >
                        {dayjs(sub.payment_failed_at).format('DD/MM/YY')}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="text-nowrap">
                    {sub.status === 'cancelled' && sub.cancelled_at ? (
                      <span
                        title={[
                          sub.cancellation_reason
                            ? `Reason: ${sub.cancellation_reason}`
                            : null,
                          sub.cancellation_notes
                            ? `Notes: ${sub.cancellation_notes}`
                            : null
                        ]
                          .filter(Boolean)
                          .join('\n')}
                      >
                        {dayjs(sub.cancelled_at).format('DD/MM/YY')}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{dayjs(sub.created_at).format('DD/MM/YYYY')}</td>
                  <td>
                    {(isOrganizationAdmin || isAdmin) &&
                      (sub.status === 'active' ||
                        sub.status === 'past_due') && (
                        <Button
                          size="sm"
                          variant="danger"
                          disabled={isCancelling}
                          onClick={() =>
                            handleCancelClick(sub.stripe_subscription_id)
                          }
                        >
                          Cancel
                        </Button>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <CancellationReasonModal
        show={showCancelModal}
        onHide={() => {
          setShowCancelModal(false);
          setSelectedSubscriptionId(null);
        }}
        handleConfirm={handleConfirmCancel}
        loading={isCancelling}
        title="Cancel Subscription"
        confirmText="Cancel Subscription"
        staffName={staffName}
      />
    </>
  );
};

export default DonorSubscriptions;
